import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="section-container py-12 sm:py-20">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-8 hover:bg-transparent hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-12">
            Privacy Policy
          </h1>

          <div className="space-y-12 text-left">
            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">1. Introduction</h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                TenderAI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered tender review and bid intelligence platform.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">2. Information We Collect</h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-4">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 text-base text-muted-foreground leading-relaxed space-y-2">
                <li>Account information (name, email address, company details)</li>
                <li>Tender documents and related files you upload</li>
                <li>Usage data and analytics</li>
                <li>Communication preferences</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">3. How We Use Your Information</h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-base text-muted-foreground leading-relaxed space-y-2">
                <li>Provide and improve our AI tender review services</li>
                <li>Process and analyze your tender documents</li>
                <li>Communicate with you about our services</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">4. Data Security</h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal data, including encryption (AES-256), secure data storage, regular security audits, and ISO 27001 certification.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">5. Data Retention</h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">6. Your Rights</h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-4">
                Under GDPR and other applicable laws, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-base text-muted-foreground leading-relaxed space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to data processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl mb-4">7. Contact Us</h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy, please contact us at:
              </p>
              <p className="text-base text-muted-foreground leading-relaxed mt-4">
                Email: info@smarttenderai.com<br />
                Phone: +44 20 3976 0700<br />
                Address: 3 Shortlands, London W6 8DA, UK
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
